import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle2, DoorOpen, Utensils, XCircle, Ghost, Send, Trash2,
  RefreshCw, Search as SearchIcon, Plus, Copy as CopyIcon, MessageCircle,
  List as ListIcon, CalendarDays, CalendarRange, Pencil, Copy, Move, Link2,
  MoreVertical,
} from "lucide-react";
import ReservationCalendar from "@/pages/ReservationCalendar";
import EditReservationDrawer from "@/components/reservations/EditReservationDrawer";
import EventDialog from "@/components/reservations/EventDialog";
import ReservationFormOverlay from "@/components/reservations/ReservationFormOverlay";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { romeToday } from "@/components/agenda/agendaConfig";
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ---- Config statica del modulo (cache lazy) ----
let _cfgCache = null;
async function getCfg() {
  if (_cfgCache) return _cfgCache;
  const r = await api.get("/admin/reservations/config");
  _cfgCache = r.data;
  return _cfgCache;
}

// One-click transitions per riga: le mostro solo se coerenti col status corrente.
const NEXT_ACTIONS = {
  da_confermare: [
    { to: "confermata", label: "Conferma", Icon: CheckCircle2, tone: "primary" },
    { to: "annullata",  label: "Annulla",  Icon: XCircle,       tone: "danger"  },
  ],
  confermata: [
    { to: "arrivato",  label: "Arrivato", Icon: DoorOpen,   tone: "primary" },
    { to: "no_show",   label: "No-show",  Icon: Ghost,       tone: "danger"  },
    { to: "annullata", label: "Annulla",  Icon: XCircle,     tone: "danger"  },
  ],
  arrivato: [
    { to: "completata", label: "Completa", Icon: Utensils, tone: "primary" },
    { to: "annullata",  label: "Annulla",  Icon: XCircle,  tone: "danger"  },
  ],
  completata: [],
  annullata:  [],
  no_show:    [],
};

const STATUS_STYLES = {
  da_confermare: "bg-amber-100 text-amber-900 border-amber-300",
  confermata:    "bg-emerald-100 text-emerald-900 border-emerald-300",
  arrivato:      "bg-sky-100 text-sky-900 border-sky-300",
  completata:    "bg-neutral-200 text-neutral-800 border-neutral-300",
  annullata:     "bg-rose-100 text-rose-900 border-rose-300",
  no_show:       "bg-orange-100 text-orange-900 border-orange-300",
};

const STATUS_LABELS = {
  da_confermare: "Da confermare",
  confermata: "Confermata", arrivato: "Arrivato",
  completata: "Completata",
  annullata: "Annullata", no_show: "No-show",
};

const SCOPES = [
  { key: "today",    label: "Oggi" },
  { key: "tomorrow", label: "Domani" },
  { key: "week",     label: "7 giorni" },
  { key: "date",     label: "Data…" },
  { key: "all",      label: "Tutte" },
];

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("it-IT", {
      weekday: "short", day: "2-digit", month: "short",
    });
  } catch { return d; }
}

// -----------------------------------------------------------------------------
// PAGE
// -----------------------------------------------------------------------------
export default function AdminReservations() {
  const { user } = useAuth();
  const canCreate = hasPermission(user, "booking.create");
  const canEdit = hasPermission(user, "booking.edit");
  const canCancel = hasPermission(user, "booking.cancel");
  const canDelete = ["admin", "super_admin"].includes(user?.role);
  // Eventi calendario: nessun permesso dedicato, backend = ruolo ≥ manager
  const canEvents = ["manager", "admin", "super_admin"].includes(user?.role);
  const [view, setView] = useState("list");  // list | day | week
  const [calendarDate, setCalendarDate] = useState(romeToday);
  const [scope, setScope] = useState("today");
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [cfg, setCfg] = useState(null);
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  // Prefill data for the create dialog when the user double-clicks a slot.
  const [createInit, setCreateInit] = useState(null);
  // Event dialog state: null = closed, {} = new, {event} = edit.
  const [eventDlg, setEventDlg] = useState(null);

  // ------------------------------------------------------------------
  // Event handlers reused across day/week views
  // ------------------------------------------------------------------
  const reloadEvents = useCallback(async () => {
    if (view === "list") return;
    let df, dt;
    if (view === "day") { df = dt = calendarDate; }
    else {
      const d = new Date(calendarDate + "T12:00:00Z");
      const dow = (d.getUTCDay() + 6) % 7;
      d.setUTCDate(d.getUTCDate() - dow);
      df = d.toISOString().slice(0, 10);
      d.setUTCDate(d.getUTCDate() + 6);
      dt = d.toISOString().slice(0, 10);
    }
    try {
      const r = await api.get(`/admin/calendar-events?date_from=${df}&date_to=${dt}`);
      setEvents(r.data || []);
    } catch { setEvents([]); }
  }, [view, calendarDate]);

  const moveEvent = useCallback(async (ev, newDate) => {
    const prev = { date: ev.date };
    try {
      await api.patch(`/admin/calendar-events/${ev.id}`, { date: newDate });
      toast.success(
        <div className="flex items-center gap-3">
          <span>{ev.title} → {new Date(newDate + "T00:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</span>
          <button data-testid={`cal-event-undo-${ev.id}`}
                    className="text-xs px-2 py-1 rounded bg-white/20 text-white hover:bg-white/30"
                    onClick={async () => {
                      try {
                        await api.patch(`/admin/calendar-events/${ev.id}`, prev);
                        toast.success("Spostamento evento annullato");
                        reloadEvents();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}>
            Annulla
          </button>
        </div>,
        { duration: 8000 });
      reloadEvents();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [reloadEvents]);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Calendar views override scope: fetch just the visible range.
      if (view === "day") {
        params.set("scope", "date");
        params.set("date", calendarDate);
      } else if (view === "week") {
        // Solo la settimana visibile (lun–dom): nessuna prenotazione persa per il limite di risultati.
        const d = new Date(calendarDate + "T12:00:00Z");
        d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
        params.set("scope", "");
        params.set("date_from", d.toISOString().slice(0, 10));
        d.setUTCDate(d.getUTCDate() + 6);
        params.set("date_to", d.toISOString().slice(0, 10));
      } else {
        if (scope) params.set("scope", scope === "all" ? "" : scope);
        if (scope === "date" && dateFilter) params.set("date", dateFilter);
      }
      if (statusFilter) params.set("status", statusFilter);
      if (q.trim()) params.set("q", q.trim());
      const [rowsRes, statsRes] = await Promise.all([
        api.get(`/admin/reservations?${params.toString()}`),
        api.get("/admin/reservations/stats"),
      ]);
      setRows(rowsRes.data || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [view, calendarDate, scope, dateFilter, statusFilter, q]);

  useEffect(() => { getCfg().then(setCfg).catch(() => {}); }, []);
  const waAvailable = Boolean(cfg?.whatsapp_confirm?.available);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh cadenza servizio
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { reloadEvents(); }, [reloadEvents]);

  const changeStatus = async (r, to, opts = {}) => {
    try {
      await api.patch(`/admin/reservations/${r.id}/status`,
                       { status: to, force: !!opts.force });
      toast.success(`${r.customer_name}: ${STATUS_LABELS[to]}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resend = async (r) => {
    if (!waAvailable) { return; }
    try {
      const rr = await api.post(`/admin/reservations/${r.id}/resend`, { client_request_id: `ui-${r.id}-${Date.now()}` });
      const n = rr.data?.notification;
      if (n?.status === "accepted") { toast.success("Richiesta accettata da Meta: esito in arrivo"); }
      else { toast.error(n?.error || "Invio non riuscito"); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeR = async (r) => {
    if (!window.confirm(`Eliminare la prenotazione ${r.code}?`)) return;
    try {
      await api.delete(`/admin/reservations/${r.id}`);
      toast.success("Prenotazione eliminata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const duplicateR = (r) => {
    // Form unico Reservations V2 precompilato; la richiesta WhatsApp non viene ereditata.
    setCreateInit({
      date: r.date, time: r.time, pax: r.pax,
      customer_name: r.customer_name, customer_surname: r.customer_surname,
      customer_phone: r.customer_phone, notes: r.notes || "", booking_source: r.booking_source,
    });
    setCreating(true);
  };

  const copyLinkR = async (r) => {
    try {
      const res = await api.get(`/admin/reservations/${r.id}`);
      const url = res.data?.confirm_url;
      if (!url) { toast.error("Link non disponibile"); return; }
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link conferma copiato");
      } catch {
        window.prompt("Copia il link (Ctrl+C):", url);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-reservations-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">CRM v5</div>
          <h1 className="font-serif text-3xl mt-1">Agenda Prenotazioni</h1>
          <p className="text-sm text-ink-500 mt-1">Gestionale live per il servizio · 1-click, senza popup.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="resv-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna
          </Button>
          {canEvents && (
          <Button variant="outline"
                    onClick={() => setEventDlg({ defaultDate: calendarDate })}
                    data-testid="resv-new-event-btn">
            <CalendarDays className="w-4 h-4 mr-1" /> Nuovo evento
          </Button>
          )}
          {canCreate && (
          <Button onClick={() => setCreating(true)} data-testid="resv-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuova prenotazione
          </Button>
          )}
        </div>
      </header>

      <StatsHeader stats={stats} />

      {/* View switcher */}
      <div className="inline-flex rounded-full border border-sand-300 bg-white p-1"
           data-testid="resv-view-switcher">
        {[
          { k: "list",  label: "Lista",      Icon: ListIcon },
          { k: "day",   label: "Giorno",     Icon: CalendarDays },
          { k: "week",  label: "Settimana",  Icon: CalendarRange },
        ].map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setView(k)}
                  data-testid={`resv-view-${k}`}
                  className={`text-xs rounded-full px-3 py-1.5 inline-flex items-center gap-1
                    ${view === k ? "bg-bronze-600 text-white" : "text-ink-600 hover:bg-sand-100"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {view !== "list" ? (
        <ReservationCalendar
          reservations={rows}
          events={events}
          view={view}
          selectedDate={calendarDate}
          onDateChange={setCalendarDate}
          onUpdated={load}
          onOpenReservation={(id) => setDetailId(id)}
          onCreateAt={(date, time) => { setCreateInit({ date, time }); setCreating(true); }}
          onOpenEvent={(ev) => setEventDlg({ event: ev })}
          onCreateEvent={(date) => setEventDlg({ defaultDate: date })}
          onEventMove={moveEvent}
        />
      ) : (
      <>
      {/* Filters (list only) */}
      <div className="rounded-2xl border border-sand-300 bg-white p-3 flex flex-wrap gap-2 items-center">
        {SCOPES.map((s) => (
          <button key={s.key}
            data-testid={`resv-scope-${s.key}`}
            onClick={() => setScope(s.key)}
            className={`text-xs rounded-full px-3 py-1.5 border ${scope === s.key ? "bg-bronze-600 text-white border-bronze-600" : "border-sand-300 hover:bg-sand-100"}`}>
            {s.label}
          </button>
        ))}
        {scope === "date" && (
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
            className="w-40" data-testid="resv-date-picker" />
        )}
        <select className="text-sm border border-sand-300 rounded-lg px-2 py-1.5"
                value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="resv-status-filter">
          <option value="">Tutti gli stati</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <div className="ml-auto relative">
          <SearchIcon className="absolute left-2 top-2.5 w-4 h-4 text-ink-400" />
          <Input placeholder="Cerca nome, telefono, codice…" value={q}
                 onChange={(e) => setQ(e.target.value)} className="pl-8 w-64"
                 data-testid="resv-search" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-sand-300 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sand-100 text-ink-500 text-left">
              <tr>
                <th className="px-3 py-2">Codice</th>
                <th className="px-3 py-2">Data / Ora</th>
                <th className="px-3 py-2">Cliente</th>
                <th className="px-3 py-2">Pax</th>
                <th className="px-3 py-2">Zona</th>
                <th className="px-3 py-2">Stato</th>
                <th className="px-3 py-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody data-testid="resv-list">
              {loading && (
                <tr><td colSpan={7} className="text-center py-8 text-ink-400">Caricamento…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-ink-400"
                        data-testid="resv-empty">
                  Nessuna prenotazione per i filtri correnti.
                </td></tr>
              )}
              {rows.map((r) => (
                <ContextMenu key={r.id}>
                  <ContextMenuTrigger asChild>
                <tr className="border-t border-sand-200 hover:bg-sand-50 cursor-pointer"
                    onDoubleClick={() => setDetailId(r.id)}
                    data-testid={`resv-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs tabular-nums">{r.code}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-sans tabular-nums">
                    <div>{fmtDate(r.date)}</div>
                    <div className="text-ink-500">{r.time}</div>
                  </td>
                  <td className="px-3 py-2">
                    <button onClick={() => setDetailId(r.id)}
                            className="text-left hover:underline"
                            data-testid={`resv-open-${r.id}`}>
                      <div className="font-medium">{r.customer_name} {r.customer_surname || ""}</div>
                      <div className="text-xs text-ink-500 font-sans tabular-nums">{r.customer_phone}</div>
                    </button>
                    {(r.allergies || []).length > 0 && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {r.allergies.slice(0, 3).map((a) => (
                          <Badge key={a} variant="outline" className="text-[10px] border-rose-300 text-rose-800">⚠ {a}</Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums font-medium font-sans">{r.pax}</td>
                  <td className="px-3 py-2 text-ink-600">{r.zone || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-xs rounded-full border px-2 py-0.5 ${STATUS_STYLES[r.status]}`}
                          data-testid={`resv-status-${r.id}`}>
                      {STATUS_LABELS[r.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      {canEdit && (NEXT_ACTIONS[r.status] || []).filter(({ to }) => canCancel || to !== "annullata").map(({ to, label, Icon, tone }) => (
                        <button key={to} onClick={() => changeStatus(r, to)}
                                data-testid={`resv-act-${r.id}-${to}`}
                                className={`text-xs rounded-full px-2 py-1 border inline-flex items-center gap-1
                                  ${tone === "danger" ? "border-rose-300 text-rose-800 hover:bg-rose-50" : "border-emerald-400 text-emerald-800 hover:bg-emerald-50"}`}>
                          <Icon className="w-3 h-3" /> {label}
                        </button>
                      ))}
                      {canEdit && (canCancel || r.status !== "annullata") && <>
                      <StatusOverrideSelect
                        current={r.status}
                        canCancel={canCancel}
                        onChange={(to) => changeStatus(r, to, { force: true })}
                        testId={`resv-override-${r.id}`}
                      />
                      <button onClick={() => resend(r)} title={waAvailable ? "Rinvia messaggio" : "Disponibile dopo attivazione WhatsApp"}
                              disabled={!waAvailable}
                              data-testid={`resv-resend-${r.id}`}
                              className="text-xs rounded-full px-2 py-1 border border-sand-300 hover:bg-sand-100 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                        <Send className="w-3 h-3" />
                      </button>
                      </>}
                      <button onClick={() => removeR(r)} title="Elimina"
                              data-testid={`resv-delete-${r.id}`}
                              className="text-xs rounded-full px-2 py-1 border border-rose-300 text-rose-800 hover:bg-rose-50 inline-flex items-center gap-1 hidden">
                        <Trash2 className="w-3 h-3" />
                      </button>

                      {/* Kebab ⋮ — menu contestuale sempre visibile con azioni secondarie */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid={`resv-kebab-${r.id}`}
                            title="Altre azioni"
                            className="text-xs rounded-full w-7 h-7 border border-sand-300 hover:bg-sand-100 inline-flex items-center justify-center">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" data-testid={`resv-kebab-menu-${r.id}`}>
                          <DropdownMenuItem onSelect={() => setDetailId(r.id)}
                                              data-testid={`resv-kebab-edit-${r.id}`}>
                            <Pencil className="w-3.5 h-3.5 mr-2" /> {canEdit ? "Modifica" : "Dettagli"}
                          </DropdownMenuItem>
                          {canCreate && (
                          <DropdownMenuItem onSelect={() => duplicateR(r)}
                                              data-testid={`resv-kebab-duplicate-${r.id}`}>
                            <Copy className="w-3.5 h-3.5 mr-2" /> Duplica
                          </DropdownMenuItem>
                          )}
                          {canEdit && (
                          <DropdownMenuItem onSelect={() => setDetailId(r.id)}
                                              data-testid={`resv-kebab-move-${r.id}`}>
                            <Move className="w-3.5 h-3.5 mr-2" /> Sposta (data/ora)
                          </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onSelect={() => copyLinkR(r)}
                                              data-testid={`resv-kebab-link-${r.id}`}>
                            <Link2 className="w-3.5 h-3.5 mr-2" /> Copia link conferma
                          </DropdownMenuItem>
                          {canEdit && (
                          <DropdownMenuItem onSelect={() => resend(r)} disabled={!waAvailable}
                                              data-testid={`resv-kebab-resend-${r.id}`}>
                            <Send className="w-3.5 h-3.5 mr-2" /> {waAvailable ? "Rinvia messaggio" : "Rinvia (dopo attivazione WhatsApp)"}
                          </DropdownMenuItem>
                          )}
                          {canDelete && <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => removeR(r)}
                                              className="text-rose-700 focus:text-rose-800"
                                              data-testid={`resv-kebab-delete-${r.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Elimina
                          </DropdownMenuItem>
                          </>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
                  </ContextMenuTrigger>
                  <ContextMenuContent data-testid={`resv-ctx-${r.id}`}>
                    <ContextMenuItem onSelect={() => setDetailId(r.id)}
                                      data-testid={`resv-ctx-edit-${r.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-2" /> {canEdit ? "Modifica" : "Dettagli"}
                    </ContextMenuItem>
                    {canCreate && (
                    <ContextMenuItem onSelect={() => duplicateR(r)}
                                      data-testid={`resv-ctx-duplicate-${r.id}`}>
                      <Copy className="w-3.5 h-3.5 mr-2" /> Duplica
                    </ContextMenuItem>
                    )}
                    {canEdit && (
                    <ContextMenuItem onSelect={() => { setDetailId(r.id); }}
                                      data-testid={`resv-ctx-move-${r.id}`}>
                      <Move className="w-3.5 h-3.5 mr-2" /> Sposta (data/ora)
                    </ContextMenuItem>
                    )}
                    <ContextMenuItem onSelect={() => copyLinkR(r)}
                                      data-testid={`resv-ctx-link-${r.id}`}>
                      <Link2 className="w-3.5 h-3.5 mr-2" /> Copia link conferma
                    </ContextMenuItem>
                    {canDelete && <>
                    <ContextMenuSeparator />
                    <ContextMenuItem onSelect={() => removeR(r)}
                                      className="text-rose-700 focus:text-rose-800"
                                      data-testid={`resv-ctx-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Elimina
                    </ContextMenuItem>
                    </>}
                  </ContextMenuContent>
                </ContextMenu>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {creating && (
        <ReservationFormOverlay entryPoint="admin_agenda" prefill={createInit || { date: calendarDate }}
          backLabel="Agenda"
          onClose={() => { setCreating(false); setCreateInit(null); }}
          onSaved={(res) => {
            setCreating(false); setCreateInit(null);
            if (res?.date) {
              setCalendarDate(res.date);
              if (view === "list") { setScope("date"); setDateFilter(res.date); }
            }
            load();
          }} />
      )}

      {detailId && (
        <EditReservationDrawer id={detailId} cfg={cfg}
          onClose={() => setDetailId(null)} onChanged={load} />
      )}

      {eventDlg && (
        <EventDialog event={eventDlg.event}
          defaultDate={eventDlg.defaultDate}
          onClose={() => setEventDlg(null)}
          onChanged={reloadEvents} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// StatusOverrideSelect — dropdown compatto per correggere lo stato in caso
// di errore. Bypassa la state machine con `force=true` sulla PATCH.
// -----------------------------------------------------------------------------
function StatusOverrideSelect({ current, onChange, testId, canCancel = true }) {
  return (
    <label className="text-xs inline-flex items-center" title="Modifica stato (correzione errore)">
      <select
        data-testid={testId}
        value=""
        onChange={(e) => {
          const to = e.target.value;
          if (!to || to === current) return;
          if (!window.confirm(`Cambiare stato in "${STATUS_LABELS[to]}"?\n\nUsa questa opzione solo per correggere un errore.`)) {
            e.target.value = "";
            return;
          }
          onChange(to);
          e.target.value = "";
        }}
        className="text-xs rounded-full pl-6 pr-2 py-1 border border-sand-300 bg-white hover:bg-sand-100 cursor-pointer appearance-none bg-no-repeat"
        style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M12 20h9'/><path d='M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z'/></svg>\")",
          backgroundPosition: "6px center",
          backgroundSize: "12px 12px",
        }}
      >
        <option value="">Modifica</option>
        {Object.entries(STATUS_LABELS)
          .filter(([k]) => k !== current && (canCancel || k !== "annullata"))
          .map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
      </select>
    </label>
  );
}

// -----------------------------------------------------------------------------
function StatsHeader({ stats }) {
  if (!stats) return null;
  const t = stats.today || {};
  const tm = stats.tomorrow || {};
  const kpis = [
    { label: "Coperti oggi",  value: t.total_pax || 0, testid: "kpi-pax-today" },
    { label: "Prenotazioni oggi", value: t.total || 0, testid: "kpi-count-today" },
    { label: "Coperti domani", value: tm.total_pax || 0, testid: "kpi-pax-tomorrow" },
    { label: "No-show 30gg", value: `${stats.no_show_rate_30d ?? 0}%`, testid: "kpi-noshow" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {kpis.map((k) => (
        <div key={k.label} data-testid={k.testid}
             className="rounded-2xl border border-sand-300 bg-white p-4">
          <div className="text-xs text-ink-500">{k.label}</div>
          <div className="mt-1 font-sans text-3xl tabular-nums font-semibold">{k.value}</div>
        </div>
      ))}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs text-ink-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function DetailDialog({ id, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get(`/admin/reservations/${id}`).then((res) => {
      if (mounted) { setR(res.data); setLoading(false); }
    }).catch((e) => { toast.error(formatApiError(e)); onClose(); });
    return () => { mounted = false; };
  }, [id, onClose]);

  const copyLink = () => {
    if (!r?.confirm_url) return;
    navigator.clipboard.writeText(r.confirm_url);
    toast.success("Link copiato");
  };

  if (loading || !r) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="resv-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{r.customer_name} {r.customer_surname || ""}</span>
            <span className={`text-xs rounded-full border px-2 py-0.5 ${STATUS_STYLES[r.status]}`}>
              {STATUS_LABELS[r.status]}
            </span>
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{r.code}</DialogDescription>
        </DialogHeader>
        <div className="text-sm space-y-2">
          <div><b>Data:</b> {fmtDate(r.date)} · <b>Ora:</b> {r.time} · <b>Pax:</b> {r.pax} · <b>Zona:</b> {r.zone || "—"}</div>
          <div><b>Telefono:</b> {r.customer_phone} {r.customer_email && <>· <b>Email:</b> {r.customer_email}</>}</div>
          {r.occasion && <div><b>Occasione:</b> {r.occasion}</div>}
          {(r.allergies || []).length > 0 && (
            <div><b>Allergie:</b> {r.allergies.join(", ")}</div>
          )}
          {r.notes && <div><b>Note:</b> {r.notes}</div>}
          <div className="text-xs text-ink-500">
            Messaggio: {r.confirmation_channel || "—"} · {r.confirmation_sent_at ? new Date(r.confirmation_sent_at).toLocaleString("it-IT") : "non inviato"}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Input value={r.confirm_url || ""} readOnly className="text-xs" data-testid="resv-confirm-url" />
            <Button size="sm" variant="outline" onClick={copyLink}>
              <CopyIcon className="w-3 h-3 mr-1" /> Copia
            </Button>
          </div>
          <div className="pt-2">
            <div className="text-xs text-ink-500 mb-1">Cronologia stati</div>
            <ul className="space-y-1">
              {(r.status_history || []).map((h, i) => (
                <li key={i} className="text-xs text-ink-600 border-l-2 border-sand-300 pl-2">
                  <b>{STATUS_LABELS[h.status] || h.status}</b> · {new Date(h.at).toLocaleString("it-IT")}
                  {h.actor_email && <> · <i>{h.actor_email}</i></>}
                  {h.note && <> · {h.note}</>}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
